import React, { FC, Fragment } from 'react';
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel } from '@mui/material';
import { Order } from 'models/order';
import { useTranslation } from 'react-i18next';
import DeliveryScheduleTableItem from 'components/delivery-schedule/delivery-schedule-table-item.component';
import PageTitle from 'components/shared/page-title/page-title.component';
import InfiniteScroll from 'react-infinite-scroll-component';
import useFixMissingScroll from 'utils/use-fix-missing-scroll';
import { OrderSortColumn, SortDirection } from 'components/delivery-schedule/use-order-sort.hook';

type NoDeadlineOrdersListProps = {
    onSentByClinicDialog: (order: Order) => void;
    onChangeInternalDeliveryDate?: (order: Order) => void;
    onChangeExternalDeliveryDate?: (order: Order) => void;
    onBackArrow: () => void;
    orders: Order[];
    onFetchMore: () => void;
    isExternal: boolean;
    sortColumn: OrderSortColumn;
    sortDirection: SortDirection;
    onSort: (column: OrderSortColumn) => void;
};
const NoDeadlineOrdersList: FC<NoDeadlineOrdersListProps> = ({
    onSentByClinicDialog,
    onChangeInternalDeliveryDate,
    onChangeExternalDeliveryDate,
    onBackArrow,
    orders,
    onFetchMore,
    isExternal,
    sortColumn,
    sortDirection,
    onSort,
}: NoDeadlineOrdersListProps) => {
    const { t: translate } = useTranslation(['common', 'orders', 'delivery-schedule']);

    // Fix for infinite scroll not working on some screen sizes.
    useFixMissingScroll(onFetchMore, orders?.length || 0);

    const buildSortLabel = (column: OrderSortColumn, label: string) => (
        <TableSortLabel
            active={sortColumn === column}
            direction={sortColumn === column ? sortDirection : 'asc'}
            onClick={() => onSort(column)}
        >
            {label}
        </TableSortLabel>
    );

    return (
        <Fragment>
            <PageTitle
                headingStyle="h1"
                title={translate('delivery-schedule:ORDER_WITHOUT_DEADLINE')}
                showBackButton={true}
                onBackArrow={onBackArrow}
            />
            <TableContainer component={Paper}>
                <InfiniteScroll
                    style={{ padding: 0 }}
                    dataLength={orders ? orders.length : 0}
                    next={onFetchMore}
                    throttleTimeInMs={75}
                    hasMore={true}
                    loader={undefined}
                >
                    <Table sx={{ minWidth: 650 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>{translate('common:ORDER_ID')}</TableCell>
                                <TableCell sortDirection={sortColumn === 'patient' ? sortDirection : false}>
                                    {buildSortLabel('patient', translate('common:PATIENT'))}
                                </TableCell>
                                <TableCell sortDirection={sortColumn === 'destination' ? sortDirection : false}>
                                    {buildSortLabel('destination', translate('delivery-schedule:DELIVERY_TO'))}
                                </TableCell>
                                <TableCell sortDirection={sortColumn === 'dentist' ? sortDirection : false}>
                                    {buildSortLabel('dentist', translate('common:DENTIST'))}
                                </TableCell>
                                <TableCell sortDirection={sortColumn === 'deliveryDate' ? sortDirection : false}>
                                    {buildSortLabel('deliveryDate', translate('orders:DELIVERY_DATE'))}
                                </TableCell>
                                <TableCell sortDirection={sortColumn === 'workflowStep' ? sortDirection : false}>
                                    {buildSortLabel('workflowStep', translate('orders:WORKFLOW_STEP'))}
                                </TableCell>
                                <TableCell>{translate('orders:CONSTRUCTIONS')}</TableCell>
                                <TableCell></TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {orders.map((order: Order) => (
                                <DeliveryScheduleTableItem
                                    key={order._id}
                                    order={order}
                                    onSentByClinicDialog={onSentByClinicDialog}
                                    onChangeInternalDeliveryDate={onChangeInternalDeliveryDate}
                                    onChangeExternalDeliveryDate={onChangeExternalDeliveryDate}
                                    isExternal={isExternal}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </InfiniteScroll>
            </TableContainer>
        </Fragment>
    );
};

export default NoDeadlineOrdersList;
